import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { API_BASE_URL } from "../../../config";
import { screenState, sinceSeen, when } from "./screenState";
import "../../../styles/Campaigns.css";

// The broadcaster's fleet: every screen, what is on it now, and what is waiting
// on them.
//
// This list used to show `Device.status`. Nothing writes that column except a
// Celery task no deployment runs, so a screen unplugged three days ago still
// read "active" -- the one number on the page a broadcaster would actually act
// on, and it was stale by design. The state shown here is derived from the
// screen's own check-ins by /api/vendor/overview, the same derivation the
// advertiser's campaign page uses, so the two sides of a booking cannot be told
// different stories.
//
// Rows arrive problems-first from the server and that order is kept.
const REFRESH_MS = 30000;

function money(value) {
    if (value === null || value === undefined || value === "") return null;
    return Number(value).toFixed(2);
}

export default function ManageDevices() {
    const [screens, setScreens] = useState([]);
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [editingId, setEditingId] = useState(null);
    const [rateDraft, setRateDraft] = useState("");
    const [saving, setSaving] = useState(false);
    const [rowError, setRowError] = useState("");

    const token = localStorage.getItem("token");

    const load = useCallback(async () => {
        try {
            const res = await fetch(`${API_BASE_URL}/api/vendor/overview`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) throw new Error("Could not load your screens.");
            const data = await res.json();
            setScreens(data.screens || []);
            setSummary(data.summary || null);
            setError("");
        } catch (err) {
            setError(err.message || "Could not load your screens.");
        }
        setLoading(false);
    }, [token]);

    useEffect(() => {
        load();
        const id = setInterval(load, REFRESH_MS);
        return () => clearInterval(id);
    }, [load]);

    function startEdit(screen) {
        setEditingId(screen.device_id);
        setRateDraft(screen.daily_rate != null ? String(screen.daily_rate) : "");
        setRowError("");
    }

    function cancelEdit() {
        setEditingId(null);
        setRateDraft("");
        setRowError("");
    }

    async function saveRate(screen) {
        setSaving(true);
        setRowError("");
        try {
            const res = await fetch(`${API_BASE_URL}/api/devices/${screen.device_id}`, {
                method: "PUT",
                headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                body: JSON.stringify({ daily_rate: rateDraft }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                // The server validates the rate and says why. Show that.
                setRowError(data.message || "Could not save the rate.");
                setSaving(false);
                return;
            }
            cancelEdit();
            load();
        } catch {
            setRowError("Network error. The rate was not saved.");
        }
        setSaving(false);
    }

    if (loading) return <p className="wb-sub">Loading your screens…</p>;

    const waiting = summary
        ? summary.pending_bookings + summary.pending_creative_changes
        : 0;

    return (
        <section className="wb-stack">
            <div className="wb-page-head">
                <h2>My screens</h2>
                <Link to="/vendor/register-device" className="wb-btn wb-btn--primary wb-btn--sm">
                    + Register a screen
                </Link>
            </div>

            {error && <p className="wb-note wb-note--danger">{error}</p>}

            {summary && summary.screens > 0 && (
                <div className="wb-card wb-card--flush">
                    <div className="camp-stats">
                        <div className="camp-stat">
                            <p className="camp-stat__label">Playing now</p>
                            <p className="camp-stat__value">
                                {summary.playing}<span className="wb-sub"> / {summary.screens}</span>
                            </p>
                        </div>
                        <div className="camp-stat">
                            <p className="camp-stat__label">Need attention</p>
                            <p className="camp-stat__value">{summary.needs_attention}</p>
                        </div>
                        <div className="camp-stat">
                            <p className="camp-stat__label">Waiting on you</p>
                            <p className="camp-stat__value">{waiting}</p>
                        </div>
                    </div>

                    {waiting > 0 && (
                        <div className="camp-health camp-health--issue">
                            <span>!</span>
                            <span>
                                {summary.pending_bookings > 0 && (
                                    <>{summary.pending_bookings} booking
                                        {summary.pending_bookings === 1 ? "" : "s"} to approve</>
                                )}
                                {summary.pending_bookings > 0 && summary.pending_creative_changes > 0 && " · "}
                                {summary.pending_creative_changes > 0 && (
                                    <>{summary.pending_creative_changes} ad change
                                        {summary.pending_creative_changes === 1 ? "" : "s"} to review</>
                                )}
                            </span>
                            <Link to="/vendor/requests" className="wb-btn wb-btn--secondary wb-btn--sm camp-derived">
                                Review
                            </Link>
                        </div>
                    )}
                </div>
            )}

            {!screens.length && (
                <div className="wb-card wb-empty">
                    <p className="wb-sub">No screens registered yet.</p>
                    <Link to="/vendor/register-device" className="wb-btn wb-btn--primary">
                        Register your first screen
                    </Link>
                </div>
            )}

            {screens.length > 0 && (
                <div className="wb-card wb-card--flush">
                    {screens.map((s) => {
                        const state = screenState(s.state);
                        const editing = editingId === s.device_id;
                        const rate = money(s.daily_rate);
                        return (
                            <div className="camp-screen" key={s.device_id}>
                                <div>
                                    <div className="camp-screen__name">
                                        {s.public_code && <code>{s.public_code}</code>} {s.name}
                                        {s.revoked && (
                                            <span className="wb-badge" style={{ marginLeft: "var(--s-2)" }}>
                                                Deregistered
                                            </span>
                                        )}
                                    </div>
                                    <div className="camp-screen__meta">
                                        {[s.placement_label, s.location].filter(Boolean).join(" · ") || "—"}
                                    </div>

                                    {/* The state's own explanation, where it has one. */}
                                    {state.note && !s.revoked && (
                                        <div className="camp-screen__detail">{state.note}</div>
                                    )}
                                    {s.state === "unreachable" && !s.revoked && (
                                        <div className="camp-screen__detail">
                                            Last check-in {sinceSeen(s.seconds_since_seen)}.
                                        </div>
                                    )}
                                    {s.state === "off_air" && s.next_on_air_at && (
                                        <div className="camp-screen__detail">
                                            Back on air {when(s.next_on_air_at)}.
                                        </div>
                                    )}
                                    {s.playing && (
                                        <div className="camp-screen__detail">
                                            Booking #{s.playing.request_id}
                                            {s.playing.until && ` until ${when(s.playing.until)}`}
                                        </div>
                                    )}
                                    {(s.pending_bookings > 0 || s.pending_creative_changes > 0) && (
                                        <div className="camp-screen__detail camp-health--issue">
                                            {s.pending_bookings > 0 && `${s.pending_bookings} booking to approve`}
                                            {s.pending_bookings > 0 && s.pending_creative_changes > 0 && " · "}
                                            {s.pending_creative_changes > 0 && `${s.pending_creative_changes} ad change to review`}
                                        </div>
                                    )}

                                    {/* A screen with no rate cannot be booked at all: the
                                        quote path refuses it. It looks perfectly healthy
                                        everywhere else, which is how screens sat unbookable
                                        for weeks. */}
                                    <div className="camp-screen__detail">
                                        {editing ? (
                                            <span className="wb-row" style={{ gap: "var(--s-2)" }}>
                                                <span>$</span>
                                                <input
                                                    className="wb-input wb-input--sm"
                                                    type="number"
                                                    min="0.01"
                                                    step="0.01"
                                                    autoFocus
                                                    value={rateDraft}
                                                    onChange={(e) => setRateDraft(e.target.value)}
                                                    onKeyDown={(e) => {
                                                        if (e.key === "Enter") saveRate(s);
                                                        if (e.key === "Escape") cancelEdit();
                                                    }}
                                                    style={{ width: 90 }}
                                                    aria-label={`Daily rate for ${s.name}`}
                                                />
                                                <span>per day</span>
                                                <button className="wb-btn wb-btn--primary wb-btn--sm"
                                                        onClick={() => saveRate(s)} disabled={saving}>
                                                    {saving ? "Saving…" : "Save"}
                                                </button>
                                                <button className="wb-btn wb-btn--ghost wb-btn--sm"
                                                        onClick={cancelEdit} disabled={saving}>
                                                    Cancel
                                                </button>
                                            </span>
                                        ) : (
                                            <span className="wb-row" style={{ gap: "var(--s-3)" }}>
                                                {rate
                                                    ? <span>${rate} per day</span>
                                                    : <strong className="camp-health--issue">
                                                        No rate — advertisers cannot book this screen
                                                      </strong>}
                                                <button className="wb-btn wb-btn--ghost wb-btn--sm"
                                                        onClick={() => startEdit(s)}>
                                                    {rate ? "Change" : "Set a rate"}
                                                </button>
                                            </span>
                                        )}
                                    </div>
                                    {editing && rowError && (
                                        <div className="camp-screen__detail camp-health--issue">{rowError}</div>
                                    )}
                                    {/* A screen with no venue type is listed with
                                        no audience information at all, which is
                                        the least attractive thing on the browse
                                        page. Flagged for the same reason a
                                        missing rate is: it looks perfectly
                                        healthy everywhere else, and nothing else
                                        would ever tell its host. */}
                                    {!s.revoked && !s.venue_label && (
                                        <div className="camp-screen__detail">
                                            No venue set — advertisers see nothing about
                                            who is in the room.{" "}
                                            <Link to={`/vendor-dashboard/device/${s.device_id}`}>
                                                Describe this screen
                                            </Link>
                                        </div>
                                    )}
                                </div>

                                <div className="camp-screen__right">
                                    {!s.revoked && (
                                        <span className={"wb-badge" + (state.tone ? ` wb-badge--${state.tone}` : "")}>
                                            <span className={`camp-dot camp-dot--${state.dot}`} />
                                            {state.label}
                                        </span>
                                    )}
                                    <div className="camp-screen__detail">
                                        <Link to={`/vendor-dashboard/device/${s.device_id}`}>Manage</Link>
                                    </div>
                                </div>
                            </div>
                        );
                    })}

                    <div className="camp-health camp-health--quiet">
                        <span>·</span>
                        <span>
                            Worked out from each screen's check-ins, not a live photo.
                            A screen counts as unreachable after{" "}
                            {Math.round((summary?.offline_after_seconds || 300) / 60)} minutes of silence.
                        </span>
                    </div>
                </div>
            )}
        </section>
    );
}
